// Algorithm B — Transaction-first reimbursable expense computation.
// Pure function, no imports — operates on already-loaded in-memory data.
//
// excludeAccounts: Set of account names treated as business-owned.
// Personal = NOT in excludeAccounts. Business = IN excludeAccounts.
//
// Steps:
//   B1. business_expense transactions on personal accounts  (+)
//   B2. business_expense_refund transactions on personal accounts (−)
//   B3. unpaired business_expense expense items  (+)
//   B4. unpaired business_expense_refund expense items (−)
//   B5. personal_expense transactions on business accounts (−)
//
// grandTotal = all qualifying items regardless of reimbursed flag.
// reimbursedTotal = net settled: reimbursed business_expense items minus reimbursed
//   business_expense_refund items. Mirrors the grandTotal formula so outstanding = 0
//   when the full net amount has been marked reimbursed.
// outstanding = grandTotal − reimbursedTotal.

function resolveCls(businessClassification, isRefund, transactionDirection) {
  if (businessClassification) return businessClassification;
  if (isRefund) return transactionDirection === 'credit' ? 'business_expense_refund' : 'personal_expense_refund';
  return null;
}

function txAmt(item) {
  return Math.abs(parseFloat(item.convertedTotal ?? item.total ?? item.amount ?? 0));
}

export function computeReimbursableExpenses({
  transactionFiles = [],
  expenseFiles = [],
  excludeAccounts = [],
  dateFrom,
  dateTo,
}) {
  const excludeSet = new Set(excludeAccounts);

  let grossExpenses        = 0;
  let refunds              = 0;
  let personalUseOfBiz     = 0;
  let unpairedExpenses     = 0;
  let unpairedRefunds      = 0;
  let reimbursedTotal      = 0;
  const detail             = [];

  for (const f of transactionFiles) {
    if (f.isLocal) continue;
    const accountName = f.accountName || f.name;
    const isBusiness  = excludeSet.has(accountName);
    const items       = Array.isArray(f.data) ? f.data : (() => { try { return JSON.parse(f.data); } catch { return []; } })();

    for (const tx of items) {
      if (dateFrom && tx.date < dateFrom) continue;
      if (dateTo   && tx.date > dateTo)   continue;
      const cls = resolveCls(tx.businessClassification, tx.isRefund, tx.transactionDirection);
      const amt = txAmt(tx);

      if (!isBusiness) {
        if (cls === 'business_expense') {
          grossExpenses += amt;
          detail.push({ id: String(tx.id), filename: f.name, date: tx.date, vendor: tx.vendor ?? tx.description ?? tx.name, amount: amt,  source: accountName, source_type: 'transaction', is_refund: false, reimbursed: !!tx.reimbursed });
          if (tx.reimbursed) reimbursedTotal += amt;
        }
        if (cls === 'business_expense_refund') {
          refunds += amt;
          detail.push({ id: String(tx.id), filename: f.name, date: tx.date, vendor: tx.vendor ?? tx.description ?? tx.name, amount: -amt, source: accountName, source_type: 'transaction', is_refund: true,  reimbursed: !!tx.reimbursed });
          if (tx.reimbursed) reimbursedTotal -= amt;
        }
      } else {
        if (cls === 'personal_expense') personalUseOfBiz += amt;
      }
    }
  }

  for (const f of expenseFiles) {
    const items = Array.isArray(f.data) ? f.data : (() => { try { return JSON.parse(f.data); } catch { return []; } })();
    for (const ex of items) {
      if (ex.pairedWithId) continue;
      const date = ex.date ?? ex.start_date ?? null;
      if (dateFrom && date && date < dateFrom) continue;
      if (dateTo   && date && date > dateTo)   continue;
      const cls = resolveCls(ex.businessClassification, ex.isRefund, ex.transactionDirection);
      const amt = txAmt(ex);

      if (cls === 'business_expense') {
        unpairedExpenses += amt;
        detail.push({ id: String(ex.id), filename: f.name, date, vendor: ex.vendor, amount: amt,  source: f.name, source_type: 'expense_report', is_refund: false, reimbursed: !!ex.reimbursed });
        if (ex.reimbursed) reimbursedTotal += amt;
      }
      if (cls === 'business_expense_refund') {
        unpairedRefunds += amt;
        detail.push({ id: String(ex.id), filename: f.name, date, vendor: ex.vendor, amount: -amt, source: f.name, source_type: 'expense_report', is_refund: true,  reimbursed: !!ex.reimbursed });
        if (ex.reimbursed) reimbursedTotal -= amt;
      }
    }
  }

  const transactionNet       = parseFloat((grossExpenses - refunds).toFixed(2));
  const unpairedExpenseItems = parseFloat((unpairedExpenses - unpairedRefunds).toFixed(2));
  const grandTotal           = parseFloat((transactionNet + unpairedExpenseItems - personalUseOfBiz).toFixed(2));
  const reimbursedTotalRounded = parseFloat(reimbursedTotal.toFixed(2));

  detail.sort((a, b) => (b.date ?? '') < (a.date ?? '') ? -1 : 1);

  return {
    grandTotal,
    reimbursedTotal:      reimbursedTotalRounded,
    grossExpenses:        parseFloat(grossExpenses.toFixed(2)),
    refunds:              parseFloat(refunds.toFixed(2)),
    transactionNet,
    unpairedExpenseItems,
    personalUseOfBiz:     parseFloat(personalUseOfBiz.toFixed(2)),
    detail,
  };
}
